import { z } from "zod";
import { UploadContextType } from "../enum.js";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_VIDEO_SIZE = 200 * 1024 * 1024;
const MAX_FILE_SIZE = 25 * 1024 * 1024;

const IMAGE_CONTENT_TYPES = ["image/png", "image/jpeg"] as const;
const VIDEO_CONTENT_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;
const FILE_CONTENT_TYPES = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.ms-powerpoint",
	"application/vnd.openxmlformats-officedocument.presentationml.presentation",
	"application/zip",
	"application/x-zip-compressed",
	"text/plain",
	"text/csv",
] as const;
const ALL_CONTENT_TYPES = [...IMAGE_CONTENT_TYPES, ...VIDEO_CONTENT_TYPES, ...FILE_CONTENT_TYPES] as const;

const IMAGE_ONLY_CONTEXTS: UploadContextType[] = [
	UploadContextType.USER_AVATAR,
	UploadContextType.COMMUNITY_AVATAR,
	UploadContextType.COMMUNITY_COVER,
	UploadContextType.EVENT_COVER,
	UploadContextType.MEETING_COVER,
	UploadContextType.SPONSORED_MEDIA,
	UploadContextType.COMMUNITY_MOBILE_APP_ICON,
	UploadContextType.COMMUNITY_MOBILE_APP_SPLASH,
	UploadContextType.COMMUNITY_MOBILE_APP_ADAPTIVE_ICON,
];

const isFileContentType = (contentType: string): boolean => (FILE_CONTENT_TYPES as readonly string[]).includes(contentType);

const uploadContextSchema = z.object({
	type: z.enum(Object.values(UploadContextType) as [UploadContextType, ...UploadContextType[]]),
	entity_id: z.uuid({ message: "Entity ID must be a valid UUID" }).optional(),
});

export type UploadContext = z.infer<typeof uploadContextSchema>;

export const presignUploadSchema = z
	.object({
		file_name: z.string().min(1, { message: "File name is required" }),
		content_type: z.enum(ALL_CONTENT_TYPES, {
			message: "Only PNG/JPEG images, MP4/WebM/QuickTime videos, and PDF/DOC/XLS/PPT/ZIP/TXT/CSV files are allowed",
		}),
		size: z.number().positive({ message: "File size must be positive" }),
		context: uploadContextSchema,
		width: z.number().int().positive().optional(),
		height: z.number().int().positive().optional(),
	})
	.strict()
	.refine(
		(data) => {
			if (data.context.type === UploadContextType.USER_AVATAR) return true;
			return !!data.context.entity_id;
		},
		{ message: "Entity ID is required for this upload context", path: ["context", "entity_id"] },
	)
	.refine(
		(data) => {
			if (IMAGE_ONLY_CONTEXTS.includes(data.context.type)) {
				return (IMAGE_CONTENT_TYPES as readonly string[]).includes(data.content_type);
			}
			return true;
		},
		{ message: "Only PNG and JPEG images are allowed for this upload context", path: ["content_type"] },
	)
	.refine(
		(data) => {
			if (isFileContentType(data.content_type)) return data.size <= MAX_FILE_SIZE;
			const isVideo = data.content_type.startsWith("video/");
			const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
			return data.size <= maxSize;
		},
		{ message: "File size exceeds the maximum allowed", path: ["size"] },
	);

export type PresignUploadPayload = z.infer<typeof presignUploadSchema>;

export const presignBatchUploadSchema = z
	.array(presignUploadSchema)
	.min(1, { message: "At least one file is required" })
	.max(6, { message: "Maximum 6 files per batch" });

export type PresignBatchUploadPayload = z.infer<typeof presignBatchUploadSchema>;

export interface PresignUploadResponse {
	upload_url: string;
	public_url: string;
}
